import { useCallback, useEffect, useState } from "react";
import i18n from "../i18n";

export const PREFERENCES_KEY = "hightekers.user.preferences";

export interface UserPreferences {
  compactRows: boolean;
  language: "en" | "fr";
}

const DEFAULT: UserPreferences = { compactRows: false, language: "en" };

function readLS(): UserPreferences {
  if (typeof window === "undefined") return DEFAULT;
  try {
    const raw = window.localStorage.getItem(PREFERENCES_KEY);
    if (!raw) return DEFAULT;
    const parsed = JSON.parse(raw) as Partial<UserPreferences>;
    // Validate language against the union — rejects garbage from manually
    // edited localStorage so the lang attribute never gets an invalid value
    // (would mislead screen readers).
    const language: UserPreferences["language"] =
      parsed.language === "en" || parsed.language === "fr"
        ? parsed.language
        : DEFAULT.language;
    return { ...DEFAULT, ...parsed, language };
  } catch {
    return DEFAULT;
  }
}

function writeLS(prefs: UserPreferences): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PREFERENCES_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore quota */
  }
}

/**
 * Persists user preferences to localStorage.
 * Applies compact-rows by toggling data-compact on <html>.
 * Applies language by setting document.documentElement.lang.
 * Syncs across tabs via the storage event.
 */
export function useUserPreferences(): [UserPreferences, (patch: Partial<UserPreferences>) => void] {
  const [prefs, setState] = useState<UserPreferences>(readLS);

  useEffect(() => {
    document.documentElement.dataset.compact = prefs.compactRows ? "true" : "false";
    document.documentElement.lang = prefs.language;
    writeLS(prefs);
    // Sync the i18n instance so all t() calls update immediately.
    if (i18n.language !== prefs.language) {
      void i18n.changeLanguage(prefs.language);
    }
  }, [prefs]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === PREFERENCES_KEY) setState(readLS());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const update = useCallback(
    (patch: Partial<UserPreferences>) => setState((p) => ({ ...p, ...patch })),
    [],
  );

  return [prefs, update];
}
